 
/* eslint-disable max-len */
import { IconInfoCircleFilled, IconPlus, IconTrash } from '@tabler/icons-react';
import NSIcon from 'bricks/NSIcon/NSIcon';
import React, { useState, useMemo } from 'react';
import {
    NSTable,
    NSSelect,
    NSCellInput,
    NSDropdown,
} from 'bricks';
import RenderIf from 'components/RenderIf/RenderIf';
import { formatValue, shouldShowCents } from 'views/ProFormaTable/helpers';
import { Tooltip, UncontrolledTooltip } from 'reactstrap';
import useAuthContext from 'contexts/AuthContext';
import { FEATURE_IDS } from 'services/constants';
import { AccessLevels } from 'types/auth';
import { customStyles } from 'bricks/NSSelect/NSSelect';
import {
    currencyColumnKeys, stringColumnKeys, percentColumnKeys,
} from 'views/ProFormaTable/constants';
import {
    ProFormaGenericRow,
    ProFormaGenericColumn,
    ProFormaGenericTableProps,
} from './types';
import DeleteRowConfirmationModal from '../DeleteRowConfirmationModal/DeleteRowConfirmationModal';
import './ProFormaGenericTable.scss';
import { useProFormaSocketContext } from '../ProForma/socketContext/ProFormaSocketProvider';
import PeriodCellValue from '../ProForma/ProFormaDevelopment/DevelopmentUsesTable/PeriodCellValue/PeriodCellValue';

const getNSCellInputNumberType = (columnKey?: string) => {
    if (currencyColumnKeys.includes(columnKey || '')) {
        return 'currency';
    }
    if (percentColumnKeys.includes(columnKey || '')) {
        return 'percentage';
    }
    return undefined;
};

export const formatValueOrDash = (value: string | number | undefined): string => {
    if (value === undefined || value === null) {
        return '-';
    }

    if (typeof value === 'number' || (typeof value === 'string' && !Number.isNaN(Number(value)))) {
        return String(value);
    }

    if (typeof value === 'string') {
        return value;
    }

    return '-';
};

const isNegativeValue = (row: ProFormaGenericRow, column: ProFormaGenericColumn): boolean => {
    // Check for row-level negative flag (keep this for backward compatibility)
    if (row.isNegative) return true;

    const value = row[column.key];
    if (typeof value === 'string') {
        const numValue = parseFloat(value);
        if (!Number.isNaN(numValue)) {
            return numValue < 0;
        }
    } else if (typeof value === 'number' && column.isNegative !== false) {
        return value <= 0;
    }

    return false;
};

const isTotalValueNegative = (value: any): boolean => {
    if (typeof value === 'string') {
        const numValue = parseFloat(value);
        if (!Number.isNaN(numValue)) {
            return numValue < 0;
        }
    }
    return false;
};

const isDash = (value: any): boolean => {
    // If value is false, we treat it as not a dash (it means we want to hide it)
    if (value === false) {
        return false;
    }
    return value === '-' || value === '' || value === undefined || value === null;
};

const getCellDisplayValue = (value: any, row?: ProFormaGenericRow, column?: ProFormaGenericColumn): string => {
    // Special cases where we want to show nothing instead of a dash
    if (row && column?.key) {
        if (row.name === 'Concessions' && column.key === 'units') {
            return '';
        }
        if (row.name === 'Gain/Loss to Lease' && column.key === 'units') {
            return '';
        }
    }

    // If value is false, return empty string (hide the value)
    if (value === false) {
        return '';
    }
    return formatValueOrDash(value);
};

const shouldUseLargerCells = (row: ProFormaGenericRow, columns: ProFormaGenericColumn[], useLargerCells: boolean) => {
    if (!useLargerCells) return false;

    // Check if any column in this row has a visible dropdown
    return columns.some(col => col.dropdown?.shouldShowDropdown?.(row));
};

const ProFormaGenericTable: React.FC<ProFormaGenericTableProps> = ({
    columns,
    rows = [],
    setRows,
    defaultRow: customDefaultRow,
    addButtonLabel = 'Add new row',
    hideTotalFooter = false,
    hideAddButton = false,
    disableAddButton = false,
    disabledPlusIconTooltipMessage,
    totalText = 'Total',
    hideHeader = false,
    firstColumnWidth,
    secondColumnWidth,
    thirdColumnWidth,
    fourthColumnWidth,
    fifthColumnWidth,
    sixthColumnWidth,
    seventhColumnWidth,
    onScrollableRef,
    onScroll,
    totalValues,
    handleAddRow = () => { },
    handleDeleteRow = () => { },
    handleUpdateRow = () => { },
    numFirstColumns = 1,
    emptyRowText,
    emptyRowTitleText,
    hideCashFlowTotals = false,
    onlyTotals = false,
    fixedColumnsMaxWidth,
    useLargerCells = false,
}) => {
    const { isFeatureAccessibleToUser } = useAuthContext();
    const { showCents } = useProFormaSocketContext();
    const hasWriteAccessToProForma = isFeatureAccessibleToUser([FEATURE_IDS.PRO_FORMA], AccessLevels.READ_WRITE_ACCESS);
    const [activeInput, setActiveInput] = useState<string | undefined>(undefined);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [rowToDelete, setRowToDelete] = useState<string | null>(null);
    const [doNotShowDeleteConfirmation, setDoNotShowDeleteConfirmation] = useState(
        () => localStorage.getItem('proFormaTableDeleteConfirmation') === 'true',
    );
    const [openTooltips, setOpenTooltips] = useState<Record<string, boolean>>({});
    const toggleTooltip = (tooltipId: string) => {
        setOpenTooltips(prev => ({ ...prev, [tooltipId]: !prev[tooltipId] }));
    };

    const totals = useMemo(() => {
        if (totalValues) {
            return totalValues;
        }
        return {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, columns, totalValues]);

    const handleSetId = (id: string | undefined) => {
        setActiveInput(id);
    };

    const handleDeleteClick = (rowId: string) => {
        if (localStorage.getItem('proFormaTableDeleteConfirmation') === 'true') {
            handleDeleteRow(Number(rowId));
        } else {
            setRowToDelete(rowId);
            setShowDeleteModal(true);
        }
    };

    const handleConfirmDelete = () => {
        if (rowToDelete) {
            handleDeleteRow(Number(rowToDelete));
            setShowDeleteModal(false);
            setRowToDelete(null);
        }
    };

    const handleSetDoNotShowDeleteConfirmation = (value: boolean) => {
        setDoNotShowDeleteConfirmation(value);
        localStorage.setItem('proFormaTableDeleteConfirmation', value.toString());
    };

    const fixedColumns = columns.filter(col => col.isFixed !== false);
    const scrollableColumns = columns.filter(col => col.isFixed === false);

    const isBeforeSeparator = (column: ProFormaGenericColumn) => {
        const columnIndex = columns.findIndex(col => col.key === column.key);
        return columnIndex < numFirstColumns;
    };

    const isLastFirstColumn = (column: ProFormaGenericColumn) => {
        const columnIndex = columns.findIndex(col => col.key === column.key);
        return columnIndex === numFirstColumns - 1;
    };

    const isCellReadOnly = (row: ProFormaGenericRow, column: ProFormaGenericColumn) => {
        if (row.isReadOnly) return true;

        if (typeof column.isReadOnly === 'function') {
            return column.isReadOnly(row);
        }
        return column.isReadOnly;
    };

    // Helper to determine if negative styling should apply to this cell
    const shouldApplyNegativeStyling = (row: ProFormaGenericRow, column: ProFormaGenericColumn) => {
        // Never apply negative styling to the first column (title)
        if (isBeforeSeparator(column)) return false;

        // Check if the cell value is a dash ('-')
        const value = row[column.key];
        if (typeof value === 'string' && value === '-') {
            return true;
        }

        // For other columns, check if the row is marked as negative
        return isNegativeValue(row, column);
    };

    const getCellClassName = (row: ProFormaGenericRow, column: ProFormaGenericColumn) => {
        const classes: string[] = [];

        // All columns before the separator should be left-aligned
        if (!isBeforeSeparator(column)) {
            classes.push('text-right w-100 justify-content-end');
        }

        const value = row[column.key];

        if (isBeforeSeparator(column)) {
            classes.push('text-white');
        } else if (isDash(value) || isNegativeValue(row, column)) {
            classes.push('text-muted');
        } else {
            classes.push('text-white');
        }

        return classes.join(' ');
    };

    const renderHeaderCell = (column: ProFormaGenericColumn) => {
        if (column.headerDropdown) {
            return (
                <div className="d-flex align-items-center">
                    <div>
                        <NSDropdown
                            options={column.headerDropdown.options}
                            selectedOption={column.headerDropdown.value}
                            handleSelectOption={column.headerDropdown.onChange}
                            placeholder={column.headerDropdown.placeholder}
                            badgeColor="dark-lighten"
                            isBadgeToggle
                            useBodyContainer
                            menuRight
                            isFullWidth={false}
                        />
                    </div>
                    {column.headerDropdown.label && <div className="ml-1">{column.headerDropdown.label}</div>}
                </div>
            );
        }

        const content = column.header;
        const shouldShowAddButton = isLastFirstColumn(column) && !hideAddButton;

        if (shouldShowAddButton) {
            return (
                <>
                    <div className="d-flex align-items-center justify-content-between w-100">
                        <span>{content}</span>
                        <NSIcon
                            id="add-row-button"
                            icon={IconPlus}
                            className="ml-2 text-muted cursor--pointer"
                            onClick={disableAddButton ? undefined : handleAddRow}
                            title={addButtonLabel}
                            opacity={disableAddButton ? 0.5 : 1}
                        />
                    </div>
                    <RenderIf isTrue={!!disabledPlusIconTooltipMessage && disableAddButton}>
                        <UncontrolledTooltip
                            target="add-row-button"
                        >
                            {disabledPlusIconTooltipMessage}
                        </UncontrolledTooltip>
                    </RenderIf>
                </>
            );
        }

        return content;
    };

    const getHeaderBorderClass = (column: ProFormaGenericColumn, hideAddButton: boolean) => {
        // Add right border only to the last column before the separator
        if (isLastFirstColumn(column)) {
            return hideAddButton ? '' : 'NSTable__thead__tr__th--right-border';
        }
        return 'text-right';
    };

    const getBodyTableBorderClass = (column: ProFormaGenericColumn, row: ProFormaGenericRow, hideAddButton: boolean) => {
        // Add right border only to the last column before the separator
        if (isLastFirstColumn(column)) {
            return hideAddButton ? 'NSTable__tbody__tr' : 'NSTable__tbody__tr__td--right-border';
        }
        if (column.customBorderClassname !== undefined) {
            return typeof column.customBorderClassname === 'function' ? column.customBorderClassname(row) : column.customBorderClassname;
        }
        return 'text-right';
    };

    const handleCellChange = (rowId: number, dataKey: string) => handleUpdateRow?.(rowId, dataKey) || ((_e: React.ChangeEvent<HTMLInputElement>) => { });

    const renderDropdown = (row: ProFormaGenericRow, column: ProFormaGenericColumn) => {
        if (!column.dropdown || !column.dropdown.shouldShowDropdown?.(row)) {
            return null;
        }

        // TODO: remove isSpecialRow once the backend properly returns adjustmentType
        // and use this instead: const showSpacer = row.adjustmentType === 'Other';
        const isSpecialRow = row.name === 'Vacancy' || row.name === 'Credit loss' || row.name === 'Capital reserves' || row.name === 'Capital reserves_1' || 'suite' in row;

        return (
            <div className="d-flex">
                <NSSelect
                    name={`${row.id}-${column.key}-select`}
                    options={column.dropdown.options}
                    value={column.dropdown.getSelectedOption(row)}
                    onChange={column.dropdown.handleSelectOption(row.id)}
                    selectedItemPrefix={column.dropdown.label}
                    isClearable={false}
                    menuPosition="fixed"
                    menuPortalTarget={document.body}
                    styles={{
                        ...customStyles,
                        control: (provided: any) => ({
                            ...provided,
                            backgroundColor: '#4F5B69',
                            width: column?.dropdown?.customWidth,
                            marginTop: '4px',
                            border: 'none',
                            minHeight: '28px',
                        }),
                    }}
                />
                {!isSpecialRow && <div className="dropdown-spacer" />}
            </div>
        );
    };

    const getDefaultPlaceholder = (format?: ProFormaGenericColumn['format']) => {
        switch (format) {
            case 'ratio':
                return 'Enter ratio';
            case 'currency':
                return 'Enter amount';
            case 'percentage':
                return 'Enter percentage';
            default:
                return 'Enter value';
        }
    };

    const getLargerCellsClassName = (row: ProFormaGenericRow) => (shouldUseLargerCells(row, columns, useLargerCells) ? 'NSTable__tbody__tr__td--large' : '');

    return (
        <div className="ProFormaGenericTable">
            <div
                className="d-flex"
                style={{
                    '--first-column-width': firstColumnWidth,
                    '--second-column-width': secondColumnWidth,
                    '--third-column-width': thirdColumnWidth,
                    '--fourth-column-width': fourthColumnWidth,
                    '--fifth-column-width': fifthColumnWidth,
                    '--sixth-column-width': sixthColumnWidth,
                    '--seventh-column-width': seventhColumnWidth,
                    '--fixed-columns-max-width': fixedColumnsMaxWidth,
                } as React.CSSProperties}
            >
                {fixedColumns.length > 0 && (
                    <div className="ProFormaGenericTable__fixed-section">
                        <NSTable className="ProFormaGenericTable__table">
                            {!hideHeader && (
                                <thead className="NSTable__thead">
                                    <tr className="NSTable__thead__tr">
                                        {fixedColumns.map(column => (
                                            <th
                                                key={column.key}
                                                className={`NSTable__thead__tr__th 
                                                    ${getHeaderBorderClass(column, hideAddButton)}
                                                    ${(isBeforeSeparator(column) && column.dropdown) || (column.headerDropdown) ? 'has-dropdown' : ''}`}
                                                style={{
                                                    '--header-dropdown-width': column.headerDropdown?.width,
                                                    maxWidth: column.maxWidth,
                                                    whiteSpace: column.allowHeaderWrap ? 'normal' : 'nowrap',
                                                } as React.CSSProperties}
                                            >
                                                <div
                                                    className={`d-flex align-items-center 
                                                        ${isBeforeSeparator(column) ? 'justify-content-between' : 'justify-content-end'}`}
                                                >
                                                    {renderHeaderCell(column)}
                                                </div>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                            )}
                            {rows.length === 0 && (!hideAddButton || !!emptyRowText) ? (
                                <tbody className="NSTable__tbody">
                                    <tr className="NSTable__tbody__tr">
                                        <td colSpan={fixedColumns.length} className="text-center text-muted py-4">
                                            <div className="text-white">{emptyRowText || 'Your added rows will appear here.'}</div>
                                            <div className="small">
                                                Click the
                                                {' '}
                                                <span className="font-weight-bold text-white">[+]</span>
                                                {' '}
                                                symbol in the
                                                {' '}
                                                <span className="font-weight-bold text-white">{emptyRowTitleText || columns[0].header}</span>
                                                {' '}
                                                header to add your first source.
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            ) : (
                                <tbody className="NSTable__tbody">
                                    {rows.map(row => (
                                        <tr key={`${row.id}-${row.name}`} className="NSTable__tbody__tr">
                                            {fixedColumns.map(column => (
                                                <td
                                                    key={column.key}
                                                    className={`NSTable__tbody__tr__td ${getLargerCellsClassName(row)}
                                                        ${getBodyTableBorderClass(column, row, hideAddButton)}
                                                        ${activeInput === `${row.id}-${column.key}` ? 'pr-0' : ''}`}
                                                    style={{ maxWidth: column.maxWidth } as React.CSSProperties}
                                                >
                                                    <RenderIf isTrue={!column.isHidden}>
                                                        {isCellReadOnly(row, column) ? (
                                                            <div className={`d-flex ${isBeforeSeparator(column) && column.dropdown?.shouldShowDropdown?.(row) ? 'flex-column' : 'align-items-center'} pl-1`}>
                                                                {column.customCell && column.customCell.shouldShowCustomCell?.(row) && (
                                                                    <div className="w-100">
                                                                        {column.customCell.renderCell({
                                                                            row,
                                                                            column,
                                                                            value: row[column.key],
                                                                            onChange: handleCellChange(Number(row.id), column.key),
                                                                            activeInput,
                                                                            handleSetId,
                                                                        })}
                                                                        <RenderIf isTrue={column?.customCell?.isDeletable && !row.disableDelete}>
                                                                            <NSIcon
                                                                                icon={IconTrash}
                                                                                className="delete-icon"
                                                                                onClick={() => handleDeleteClick(row.id)}
                                                                            />
                                                                        </RenderIf>
                                                                    </div>
                                                                )}
                                                                {(!column.customCell || !column.customCell.shouldShowCustomCell?.(row)) && (
                                                                    <div className={`d-flex ${isBeforeSeparator(column) && column.dropdown?.shouldShowDropdown?.(row) ? 'flex-column' : 'align-items-center'}
                                                                        ${getLargerCellsClassName(row)} ${getCellClassName(row, column)}`}
                                                                    >
                                                                        {row.label && column.showLabel && <span className="text-muted mr-2 text-right" style={{ width: '20px' }}>{row.label}</span>}
                                                                        {formatValue(
                                                                            getCellDisplayValue(row[column.key], row, column),
                                                                            shouldApplyNegativeStyling(row, column),
                                                                            true,
                                                                            row.format || column.format,
                                                                            column.isHidden,
                                                                            shouldShowCents(column.key, showCents),
                                                                            column.customFormat,
                                                                        )}
                                                                        {column.showTooltip && row.tooltipMessage && (
                                                                            <>
                                                                                <NSIcon
                                                                                    className="px-1 text-white"
                                                                                    id={`${row.id}-${column.key}-tooltip`}
                                                                                    icon={IconInfoCircleFilled}
                                                                                />
                                                                                <Tooltip
                                                                                    target={`${row.id}-${column.key}-tooltip`}
                                                                                    isOpen={openTooltips[`${row.id}-${column.key}-tooltip`] || false}
                                                                                    toggle={() => toggleTooltip(`${row.id}-${column.key}-tooltip`)}
                                                                                    placement="top"
                                                                                >
                                                                                    <span className="font-weight-bold">{row[column.key] as string}</span>
                                                                                    <div>{row.tooltipMessage}</div>
                                                                                </Tooltip>
                                                                            </>
                                                                        )}
                                                                        {renderDropdown(row, column)}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <div
                                                                className={`d-flex ${isBeforeSeparator(column) && column.dropdown?.shouldShowDropdown?.(row) ? 'flex-column' : 'align-items-center'}
                                                                ${getLargerCellsClassName(row)} ${getCellClassName(row, column)}`}
                                                            >
                                                                {row.label && column.showLabel && <span className="text-muted mr-2">{row.label}</span>}
                                                                <div>
                                                                    <NSCellInput
                                                                        showCents={shouldShowCents(column.key, showCents)}
                                                                        type={stringColumnKeys.includes(column.key) ? 'text' : 'number'}
                                                                        numberType={getNSCellInputNumberType(column.key)}
                                                                        value={row[column.key]}
                                                                        handleUpdateValue={updatedValue => {
                                                                            const updateFn = handleUpdateRow(Number(row.id), column.key);
                                                                            if (typeof updateFn === 'function') {
                                                                                updateFn({
                                                                                    target: { value: updatedValue },
                                                                                } as React.ChangeEvent<HTMLInputElement>);
                                                                            }
                                                                        }}
                                                                        customClassName={!isBeforeSeparator(column) ? 'text-right' : 'mr-1'}
                                                                        isRightAligned={!isBeforeSeparator(column)}
                                                                        placeholder={getDefaultPlaceholder(column.format)}
                                                                    />
                                                                </div>
                                                                {renderDropdown(row, column)}
                                                                <RenderIf isTrue={isLastFirstColumn(column) && !row.isReadOnly && hasWriteAccessToProForma && !row.disableDelete}>
                                                                    <NSIcon
                                                                        icon={IconTrash}
                                                                        className="delete-icon"
                                                                        onClick={() => handleDeleteClick(row.id)}
                                                                    />
                                                                </RenderIf>
                                                            </div>
                                                        )}
                                                    </RenderIf>
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            )}
                            {!hideTotalFooter && (
                                <tfoot className="NSTable__tfoot">
                                    <tr className="NSTable__tfoot__tr">
                                        {fixedColumns.map(column => (
                                            <td
                                                key={column.key}
                                                className={`NSTable__tfoot__tr__td font-weight-bold
                                                    ${isBeforeSeparator(column) ? 'NSTable__tfoot__tr__td--right-border' : ''} 
                                                    ${isBeforeSeparator(column) ? 'text-left' : 'text-right'}`}
                                            >
                                                <div className="ProFormaGenericTable__total-cell d-flex align-items-center">
                                                    {column.key === columns[0].key
                                                        ? totalText
                                                        : (
                                                            <div className="w-100 text-right">
                                                                <div className={isDash(totals[column.key]) ? 'text-muted' : 'text-white'}>
                                                                    {formatValue(
                                                                        getCellDisplayValue(totals[column.key], undefined, column),
                                                                        isTotalValueNegative(totals[column.key]),
                                                                        true,
                                                                        column.format,
                                                                        column.isHidden,
                                                                        shouldShowCents(column.key, showCents),
                                                                        column.customFormat,
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )}
                                                </div>
                                            </td>
                                        ))}
                                    </tr>
                                </tfoot>
                            )}
                        </NSTable>
                    </div>
                )}

                {/* Scrollable Columns */}
                <RenderIf isTrue={scrollableColumns.length > 0 && !hideCashFlowTotals}>
                    <div className="ProFormaGenericTable__scrollable-section" ref={onScrollableRef} onScroll={onScroll}>
                        <NSTable className="ProFormaGenericTable__table">
                            {!hideHeader && (
                                <thead className="NSTable__thead">
                                    <tr className="NSTable__thead__tr">
                                        {scrollableColumns.map(column => (
                                            <th key={column.key} className="NSTable__thead__tr__th text-right">
                                                <div className="d-flex align-items-center justify-content-end">
                                                    <span>{column.header}</span>
                                                </div>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                            )}
                            {rows.length === 0 && (!hideAddButton || !!emptyRowText) ? (
                                <tbody className="NSTable__tbody">
                                    <tr className="NSTable__tbody__tr">
                                        <td colSpan={scrollableColumns.length} className="text-center text-muted py-4">
                                            <div>&nbsp;</div>
                                            <div className="small">&nbsp;</div>
                                        </td>
                                    </tr>
                                </tbody>
                            ) : (
                                <tbody className="NSTable__tbody">
                                    {rows.map(row => (
                                        <tr key={row.id} className="NSTable__tbody__tr">
                                            {scrollableColumns.map(column => (
                                                <td
                                                    key={column.key}
                                                    className={`NSTable__tbody__tr__td text-right ${getLargerCellsClassName(row)}
                                                        ${activeInput === `${row.id}-${column.key}` ? 'pr-0' : ''}`}
                                                    style={{ maxWidth: column.maxWidth } as React.CSSProperties}
                                                >
                                                    <div className={getCellClassName(row, column)}>
                                                        {formatValue(
                                                            getCellDisplayValue(row[column.key], row, column),
                                                            shouldApplyNegativeStyling(row, column),
                                                            true,
                                                            column.format,
                                                            column.isHidden,
                                                            shouldShowCents(column.key, showCents),
                                                            column.customFormat,
                                                        )}
                                                    </div>
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            )}
                            {!hideTotalFooter && (
                                <tfoot className="NSTable__tfoot">
                                    <tr className="NSTable__tfoot__tr">
                                        {scrollableColumns.map(column => (
                                            <td key={column.key} className="NSTable__tfoot__tr__td text-right font-weight-bold">
                                                <div className="ProFormaGenericTable__scrollable-section--total-cells">
                                                    <span className={isDash(totals[column.key]) ? 'text-muted' : 'text-white'}>
                                                        {formatValue(
                                                            getCellDisplayValue(totals[column.key], undefined, column),
                                                            isTotalValueNegative(totals[column.key]),
                                                            true,
                                                            column.format,
                                                            column.isHidden,
                                                            shouldShowCents(column.key, showCents),
                                                            column.customFormat,
                                                        )}
                                                    </span>
                                                </div>
                                            </td>
                                        ))}
                                    </tr>
                                </tfoot>
                            )}
                        </NSTable>
                    </div>
                </RenderIf>
                <RenderIf isTrue={scrollableColumns.length === 0 && !hideCashFlowTotals}>
                    <div className="ProFormaGenericTable__scrollable-section">
                        <NSTable className="ProFormaGenericTable__table-disabled">
                            {!hideHeader && (
                                <>
                                    <RenderIf isTrue={rows.length === 0 && onlyTotals}>
                                        <thead className="NSTable__thead">
                                            <tr className="NSTable__tfoot__tr">
                                                {(() => {
                                                    const periodCellKey = `empty-row-period-cell-${typeof totalText === 'string' && totalText !== 'Total' ? totalText.replaceAll(' ', '-') : columns?.[0]?.header?.toString().replaceAll(' ', '-')}`;
                                                    return (
                                                        <PeriodCellValue
                                                            key={periodCellKey}
                                                            tooltipId={periodCellKey}
                                                            period={{ label: 'MON YEAR', value: '', isPlaceholder: true }}
                                                            value="—"
                                                            cellClassName="NSTable__tbody__tr__td text-right"
                                                        />
                                                    );
                                                })()}
                                            </tr>
                                        </thead>
                                    </RenderIf>
                                    <RenderIf isTrue={!onlyTotals}>
                                        <thead className="NSTable__thead">
                                            <tr className="NSTable__thead__tr">
                                                <th
                                                    className="NSTable__thead__tr__th text-right"
                                                >
                                                    <div className="d-flex align-items-center justify-content-end">
                                                        <div className="h6 NSTable__thead__tr__th--grey-text NSTable__thead__tr__th--opacity-3  text-uppercase text-nowrap text-secondary text-right mb-0">
                                                            <div>MONTH #</div>
                                                            <div className="h6 text-uppercase text-right text-nowrap text-white mt-1">MON YEAR</div>
                                                        </div>
                                                    </div>
                                                </th>
                                            </tr>
                                        </thead>
                                    </RenderIf>
                                </>
                            )}
                            <tbody className="NSTable__tbody">
                                {rows.length === 0 && (!hideAddButton || !!emptyRowText)
                                    ? (
                                        <tr className="NSTable__tbody__tr">
                                            <PeriodCellValue
                                                key="empty-row-no-button-period-cell"
                                                tooltipId="empty-row-no-button-period-cell"
                                                period={{ label: 'MON YEAR', value: '', isPlaceholder: true }}
                                                value="—"
                                                cellClassName="text-center text-muted py-4"
                                            >
                                                <div>&nbsp;</div>
                                                <div className="small">&nbsp;</div>
                                            </PeriodCellValue>
                                        </tr>
                                    ) : (
                                        rows.map((row, index) => (
                                            <tr key={row.id} className="NSTable__tbody__tr">
                                                <PeriodCellValue
                                                    key={`empty-row-${row.id}`}
                                                    tooltipId={`empty-row-${index}-${row.id}`}
                                                    period={{ label: 'MON YEAR', value: '', isPlaceholder: true }}
                                                    value="—"
                                                    cellClassName={`NSTable__tbody__tr__td text-right ${getLargerCellsClassName(row)}`}
                                                />
                                            </tr>
                                        ))
                                    )}
                            </tbody>
                            {!hideTotalFooter && (
                                <tfoot className="NSTable__tfoot">
                                    <tr className="NSTable__tfoot__tr">
                                        <td className="NSTable__tfoot__tr__td text-right font-weight-bold">
                                            <div className="ProFormaGenericTable__scrollable-section--total-cells">
                                                <span className="text-muted" />
                                            </div>
                                        </td>
                                    </tr>
                                </tfoot>
                            )}
                        </NSTable>
                    </div>
                </RenderIf>
            </div>
            <DeleteRowConfirmationModal
                isOpen={showDeleteModal}
                toggle={() => setShowDeleteModal(false)}
                handleDelete={handleConfirmDelete}
                doNotShowAgain={doNotShowDeleteConfirmation}
                setDoNotShowAgain={handleSetDoNotShowDeleteConfirmation}
            />
        </div>
    );
};

export default ProFormaGenericTable;
